import { Clock, Effect, Result } from "effect";

import { addEntry, editEntry, getEntry } from "../../api/commands/entries";
import { addRecord, editRecord, getRecords } from "../../api/commands/records";
import { type ApiError, toMessage } from "../../api/errors";
import { randomUuid } from "../../app/idGenerator";
import type { Api } from "../shared";

/**
 * v1 `AI.tsx` `aiFunctions`, as Effects over the U3 API layer. The names and
 * argument shapes are what the realtime session's tool definitions advertise
 * (`session.ts`); anything else fails as "Unknown function".
 */

const ok = <T>(
	effect: Effect.Effect<Result.Result<T, ApiError>, never, Api>,
): Effect.Effect<T, Error, Api> =>
	effect.pipe(
		Effect.flatMap((result) =>
			Result.isSuccess(result)
				? Effect.succeed(result.success)
				: Effect.fail(new Error(toMessage(result.failure))),
		),
	);

type Args = Record<string, unknown>;

const str = (args: Args, key: string): string | undefined =>
	typeof args[key] === "string" ? (args[key] as string) : undefined;
const strings = (args: Args, key: string): ReadonlyArray<string> | undefined =>
	Array.isArray(args[key])
		? (args[key] as unknown[]).filter((v): v is string => typeof v === "string")
		: undefined;

const findRecord = (name: string) =>
	ok(getRecords.run({ search: name, cursor: 0, asOf: null })).pipe(
		Effect.flatMap((page) =>
			page.data[0] === undefined
				? Effect.fail(new Error(`No person found with name: ${name}`))
				: Effect.succeed(page.data[0]),
		),
	);

const generatedMetadata = Effect.all({
	id: randomUuid,
	timestamp: Clock.currentTimeMillis.pipe(
		Effect.map((milliseconds) => new Date(milliseconds).toISOString()),
	),
});

export const executeTool = (
	name: string,
	args: Args,
): Effect.Effect<unknown, Error, Api> => {
	switch (name) {
		case "changeTextColor": {
			const color = str(args, "color") ?? "";
			return Effect.sync(() => {
				document.body.style.color = color;
				return { success: true, color };
			});
		}
		case "addNode":
			return generatedMetadata.pipe(
				Effect.flatMap(({ id, timestamp }) =>
					ok(
						addRecord.run({
							data: {
								id,
								circleId: "1",
								name: str(args, "name") ?? "",
								type: "person",
								subtitle: "",
								description: str(args, "description") ?? "",
								tags: strings(args, "tags") ?? [],
								avatars: [],
								timestamp,
							},
						}),
					).pipe(Effect.as({ success: true })),
				),
			);
		case "addKnot": {
			const nodeName = str(args, "nodeName") ?? "";
			return findRecord(nodeName).pipe(
				Effect.flatMap((record) =>
					generatedMetadata.pipe(
						Effect.flatMap(({ id, timestamp }) =>
							ok(
								addEntry.run({
									data: {
										id,
										circleId: "1",
										nodeIds: [record.id],
										type: "note",
										title: "AI Generated Note",
										description: str(args, "note") ?? "",
										tags: strings(args, "tags") ?? [],
										timestamp,
									},
								}),
							).pipe(Effect.as({ success: true, nodeName, nodeId: record.id })),
						),
					),
				),
			);
		}
		case "editNode":
			return findRecord(str(args, "name") ?? "").pipe(
				Effect.flatMap((record) =>
					ok(
						editRecord.run({
							nodeId: record.id,
							record: {
								...record,
								name: str(args, "newName") ?? record.name,
								description:
									str(args, "description") ?? record.description ?? "",
								tags: strings(args, "tags") ?? record.tags ?? [],
							},
						}),
					).pipe(Effect.as({ success: true, nodeId: record.id })),
				),
			);
		case "editKnot": {
			const knotId = str(args, "knotId") ?? "";
			return ok(getEntry.run({ knotId, asOf: null })).pipe(
				Effect.flatMap((entry) =>
					ok(
						editEntry.run({
							knotId,
							entry: {
								...entry,
								title: str(args, "title") ?? entry.title,
								description: str(args, "description") ?? entry.description,
								tags: strings(args, "tags") ?? entry.tags ?? [],
							},
						}),
					).pipe(Effect.as({ success: true, knotId })),
				),
			);
		}
		case "navigateToPerson": {
			// v1 never navigates here either: it resolves the person and returns the id.
			const personName = str(args, "name") ?? "";
			return findRecord(personName).pipe(
				Effect.map((record) => ({
					success: true,
					name: personName,
					id: record.id,
				})),
			);
		}
		default:
			return Effect.fail(new Error(`Unknown function called: ${name}`));
	}
};
